// feedbackParts — the pieces the Overview and the Feedback page share, pure:
// the kind tag, the context chips, the kind filter chips, the sender line
// and the report's stored JSON. A report's words, context and email are the
// sender's, untrusted: always React text (escaped), never markup or a link.

export type { KindFilter };
export {
    ContextChips,
    filterByKind,
    JsonBlock,
    KindChips,
    KindTag,
    latest,
    reportJson,
    useFrom,
};

import type { FeedbackAdminReport } from "@bilda/app/feedbackAdmin";
import { exhaustSwitchCase, type Remote } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type {
    FeedbackContextItem,
    FeedbackKind,
} from "../../app/ports/feedback.js";
import { Icon, type IconName } from "../Icon/Icon.js";
import { jsonTokens } from "./jsonTokens.js";

// What a read shows: its value, or while it reads again, the value before
// (a list does not blink away after an action).
const latest = <T, E>(r: Remote.Remote<T, E>): T | undefined =>
    r._tag === "Remote.Loaded"
        ? r.value
        : r._tag === "Remote.Reloading"
          ? r.prev
          : undefined;

// One kind selected, or all (the default).
type KindFilter = "all" | FeedbackKind;

const filterByKind = (
    reports: readonly FeedbackAdminReport[],
    kind: KindFilter,
): readonly FeedbackAdminReport[] =>
    kind === "all" ? reports : reports.filter((r) => r.kind === kind);

const KIND_ICON: Record<FeedbackKind, IconName> = {
    problem: "alert",
    idea: "feedback-idea",
    question: "help",
};

const KindTag = ({ kind }: { readonly kind: FeedbackKind }) => {
    const { t } = useLingui();
    const label = (() => {
        switch (kind) {
            case "problem":
                return t`Problem`;
            case "idea":
                return t`Idea`;
            case "question":
                return t`Question`;
            default:
                return exhaustSwitchCase(kind);
        }
    })();
    return (
        <span className={`ops__tag ops__tag--${kind}`}>
            <Icon name={KIND_ICON[kind]} size="xs" />
            {label}
        </span>
    );
};

const ContextChips = ({
    context,
}: {
    readonly context: readonly FeedbackContextItem[];
}) => {
    const { t } = useLingui();
    const chip = (c: FeedbackContextItem): { icon: IconName; text: string } => {
        switch (c.type) {
            case "feedback_context_view":
                return { icon: "canvas", text: t`View: ${c.view}` };
            case "feedback_context_client":
                return {
                    icon: "globe",
                    text: t`Version ${c.version} · ${c.browser} · ${c.os}`,
                };
            case "feedback_context_error":
                return {
                    icon: "warning",
                    text:
                        c.summary === ""
                            ? t`Error ${c.errorId}`
                            : t`Error ${c.errorId} · ${c.summary}`,
                };
            default:
                return exhaustSwitchCase(c);
        }
    };
    return context.length === 0 ? null : (
        <span className="ops__context">
            {context.map((c) => {
                const { icon, text } = chip(c);
                return (
                    <span key={c.type} className="ops__context-chip">
                        <Icon name={icon} size="xs" />
                        {text}
                    </span>
                );
            })}
        </span>
    );
};

const KindChips = ({
    reports,
    value,
    onChange,
}: {
    readonly reports: readonly FeedbackAdminReport[];
    readonly value: KindFilter;
    readonly onChange: (kind: KindFilter) => void;
}) => {
    const { t } = useLingui();
    const chips: readonly {
        kind: KindFilter;
        icon: IconName;
        label: string;
    }[] = [
        { kind: "all", icon: "ops-inbox", label: t`All` },
        { kind: "problem", icon: KIND_ICON.problem, label: t`Problems` },
        { kind: "idea", icon: KIND_ICON.idea, label: t`Ideas` },
        { kind: "question", icon: KIND_ICON.question, label: t`Questions` },
    ];
    return (
        <span className="ops__chips">
            {chips.map((c) => (
                <button
                    key={c.kind}
                    type="button"
                    className="ops__chip"
                    aria-pressed={value === c.kind}
                    onClick={() => onChange(c.kind)}
                >
                    <Icon name={c.icon} size="xs" />
                    {c.label}
                    <span className="ops__chip-count">
                        {filterByKind(reports, c.kind).length}
                    </span>
                </button>
            ))}
        </span>
    );
};

// Who sent it: the email, or "Account removed" when the account is gone.
const useFrom = (): ((r: FeedbackAdminReport) => string) => {
    const { t } = useLingui();
    return (r) => r.reporterEmail ?? t`Account removed`;
};

// The report exactly as the server answered it, 2-space indented: what the
// block shows and what Copy JSON copies.
const reportJson = (r: FeedbackAdminReport): string =>
    JSON.stringify(r, null, 2);

const JsonBlock = ({ text }: { readonly text: string }) => (
    <pre className="ops__json">
        {jsonTokens(text).map((tok, i) =>
            tok.kind === "space" ? (
                tok.text
            ) : (
                // biome-ignore lint/suspicious/noArrayIndexKey: the pieces are a fixed split of one text.
                <span key={i} className={`ops__json-${tok.kind}`}>
                    {tok.text}
                </span>
            ),
        )}
    </pre>
);
